<template>
	<div class="row">
		<div
			class="contentblock"
			:class="{ reverse: index % 2 !== 0 }"
			data-aos="fade-in"
			data-aos-duration="1500"
			data-aos-once="true"
		>
			<div class="image-wrapper" :class="{ 'image-left': index % 2 === 0, 'image-right': index % 2 !== 0 }">
				<picture>
					<source :srcset="item.imageWebp" type="image/webp" />
					<img :src="item.image" :alt="item.imageAlt" loading="lazy" />
				</picture>
			</div>

			<div class="textbox">
				<h3>{{ item.header }}</h3>
				<div v-parse-links v-html="item.content" />

				<div v-if="variant === 'room'" class="room-icons">
					<div v-for="facility in item.facilities" :key="facility.id" class="icon-holder">
						<font-awesome-icon :icon="`fal fa-${facility.icon}`" class="icon" size="1x" />
						<span>{{ facility.content }}</span>
					</div>
				</div>
				<book-button v-if="variant === 'room'" class="button cta" :roomid="item.bookingid">
					{{ defaults[locale].rooms.book }}
				</book-button>
			</div>
		</div>
	</div>
</template>

<script setup>
defineProps({
	item: {
		type: Object,
		required: true,
	},
	index: { type: Number, required: true },
	variant: { type: String, default: 'promo' },
});
const { locale } = useI18n();
const { defaults } = useDefaults();
</script>

<style lang="scss" scoped>
$image-width: 60%;
$textbox-width: 45%;
$image-offset: 80px;

.row {
	margin-bottom: calc(2 * #{$image-offset} + var(--space-xl) - var(--gap));
}

.contentblock {
	display: flex;
	align-items: stretch;
	justify-content: space-between;
	flex-direction: row-reverse;
	position: relative;
	max-width: 100%;
	gap: 0;
	margin-top: $image-offset;

	&.reverse {
		flex-direction: row;

		.textbox {
			margin-right: 0;
			margin-left: 0;
			z-index: 2;
		}

		.image-wrapper {
			z-index: 1;
		}
	}

	.image-wrapper {
		position: absolute;
		top: -$image-offset;
		bottom: -$image-offset;
		width: $image-width;
		z-index: 1;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		&.image-left {
			left: 0;
			right: auto;
		}

		&.image-right {
			right: 0;
			left: auto;
		}
	}

	.textbox {
		width: $textbox-width;
		background: rgb(249 246 242 / 90%);
		backdrop-filter: blur(20px);
		padding: 80px 80px 40px;
		box-shadow: 0 10px 30px rgb(0 0 0 / 25%);
		z-index: 2;
		position: relative;

		h3 {
			font-family: var(--heading-font-family, serif);
			font-weight: 300;
			margin-bottom: 30px;
		}

		:deep(p) {
			font-size: var(--font-size-sm);
			line-height: 1.6;
			color: var(--body-color);
			margin-bottom: 40px;
		}

		.button {
			background-color: var(--color-deep-reset);
			color: white;
			padding: 10px 24px;
			font-size: 24px;
			text-decoration: none;
			display: inline-block;
		}

		.room-icons {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-start;
			align-items: flex-start;
			margin: 0 0 40px;
			gap: 20px;

			.icon {
				padding-right: 10px;
			}
		}
	}
}

@media (max-width: 910px) {
	.row {
		margin-bottom: var(--space-xxl);
	}

	.contentblock,
	.contentblock.reverse {
		flex-direction: column !important;
		margin-top: 0;

		.image-wrapper {
			position: relative;
			top: 0;
			bottom: 0;
			width: 100%;
			height: auto;

			img {
				height: auto;
			}
		}

		.textbox {
			width: 90%;
			margin: -30px auto;
			padding: 40px;
			box-shadow: none;
		}
	}
}
</style>
